// #753 — shared styles for the per-user admin sub-pages
// (Conversations / Memory / News & Topics / Settings / Health Concerns).
// Kept in one file so the datagrid + page-header + back-link styling stays
// consistent across sub-pages without 5× duplication.
//
// Datagrid contract: each long-list table is wrapped in a `.data-grid`
// container with `max-height: 360px; overflow: auto` and sticky headers,
// per Bas's "fixed height, scroll if we want to see all" requirement.
export const userSubPageStyles = `
  .back-link {
    display: inline-flex; align-items: center; gap: 6px;
    color: #888; font-size: 0.85rem; text-decoration: none;
    margin-bottom: 20px; transition: color 0.15s;
  }
  .back-link:hover { color: #E8845A; }
  .page-header {
    background: #fff; border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 20px 24px; margin-bottom: 20px;
  }
  .page-title { font-size: 1.2rem; font-weight: 700; color: #2C2C2C; }
  .page-subtitle { color: #888; font-size: 0.85rem; margin-top: 4px; }
  .ver { margin-left: 8px; font-size: 0.7rem; color: #aaa; font-weight: 400; }
  .section-title {
    font-size: 0.95rem; font-weight: 700; color: #2C2C2C;
    margin: 24px 0 12px; letter-spacing: -0.01em;
  }
  .section-sub {
    font-size: 0.8rem; color: #aaa; font-weight: 500;
    margin-left: 8px;
  }

  /* Datagrid — fixed-height scroll container with sticky header.
     Keeps long tables compact and predictable. Bas's #753 iter. */
  .data-grid {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    overflow: auto;
    max-height: 360px;
    margin-bottom: 16px;
  }
  .data-table { width: 100%; border-collapse: collapse; }
  .data-table th {
    text-align: left; padding: 10px 14px; font-size: 0.76rem;
    font-weight: 600; color: #888; text-transform: uppercase;
    letter-spacing: 0.05em; border-bottom: 1px solid #EDE9E4;
    background: #FAFAF8; white-space: nowrap;
    position: sticky; top: 0; z-index: 1;
  }
  .data-table td {
    padding: 10px 14px; font-size: 0.85rem; color: #2C2C2C;
    border-bottom: 1px solid #F0EDE8; vertical-align: top;
  }
  .data-table tr:last-child td { border-bottom: none; }
  .data-table tr.clickable { cursor: pointer; }
  .data-table tr.clickable:hover { background: #FFF9F5; }

  .badge { display: inline-block; padding: 3px 8px; border-radius: 20px; font-size: 0.75rem; font-weight: 600; }
  .badge-sensitive { background: #FFF0EE; color: #C0392B; }
  .badge-ok { background: #F0FFF4; color: #27AE60; }

  .empty-state {
    color: #aaa; font-size: 0.9rem; padding: 16px 0; font-style: italic;
  }
  .loading-state { color: #888; font-size: 0.9rem; padding: 20px 0; }
  .error-state { color: #C0392B; font-size: 0.9rem; padding: 20px 0; }

  .affinity-high { color: #27AE60; font-weight: 600; }
  .affinity-mid { color: #B8860B; font-weight: 600; }
  .affinity-low { color: #999; font-weight: 600; }
`;

export function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export function affinityClass(score) {
  if (score >= 0.6) return 'affinity-high';
  if (score >= 0.3) return 'affinity-mid';
  return 'affinity-low';
}

// Bearer-token fetch helper used by the per-user admin sub-pages that
// don't go through the adminApi wrapper. Returns the parsed JSON body
// or throws on non-2xx.
export async function adminFetch(path, opts = {}) {
  const token = localStorage.getItem('am_admin_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(opts.headers || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(path, { ...opts, headers });
  if (res.status === 401) {
    localStorage.removeItem('am_admin_token');
    window.location.href = '/admin/login';
    return null;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
}
